// src/utils/advisorOpportunities.js
// =====================================================================
// OPPORTUNITÉS DÉTECTÉES (§5) — transforme les données déjà présentes dans
// ARTKIV en BESOINS CLIENTS exploitables (missions facturables), SANS présenter
// une recommandation IA comme une décision autonome. « Opportunité détectée » /
// « besoin à vérifier », jamais « recommandation automatique ».
//
// Réutilise le MÊME moteur readiness (utils/readiness.js) — on ne recalcule pas
// des signaux parallèles. On regroupe par collection et on mappe chaque signal
// significatif vers une mission suggérée. Empreinte stable = collection + signal
// (une opportunité ignorée ne réapparaît pas identiquement, §10.3).
// =====================================================================

import { READINESS_DIMENSIONS, READINESS_SELECT_SECURE, computeCollectionReadiness } from "./readiness";

// Colonnes à charger depuis artworks_secure (RLS = périmètre du mandat) + collection_id.
export const OPPORTUNITY_SELECT_SECURE = `${READINESS_SELECT_SECURE},collection_id`;

// Signaux readiness qui deviennent des opportunités facturables, avec le type de
// mission suggéré et un rang de priorité métier (assurance/valorisation d'abord).
const SIGNAL_META = {
  valuation:     { missionType: "valuation",           rank: 0 },
  insurance:     { missionType: "insurance_file",      rank: 1 },
  transmission:  { missionType: "succession_support",  rank: 1 },
  provenance:    { missionType: "provenance_research", rank: 2 },
  documentation: { missionType: "inventory",           rank: 2 },
  conservation:  { missionType: "condition_review",    rank: 3 },
};

const DIM_BY_KEY = Object.fromEntries(READINESS_DIMENSIONS.map((d) => [d.key, d]));

const NUM = (v) => (v === null || v === undefined || v === "" ? 0 : Number(v)) || 0;
function artworkValue(a) {
  return Math.max(
    NUM(a?.estimated_value), NUM(a?.value), NUM(a?.current_estimated_value),
    NUM(a?.valuation_estimated_value), NUM(a?.valuation_consensus)
  );
}

/* ── Copie FR / EN / DE ───────────────────────────────────── */
const COPY = {
  fr: {
    collection: "Collection",
    trackedValue: "valeur suivie",
    worksConcerned: (n) => `${n} œuvre${n > 1 ? "s" : ""} concernée${n > 1 ? "s" : ""}`,
    verifyIntro: "Cette vérification peut être regroupée dans une mission",
    signals: {
      valuation:     { head: (n) => `${n} œuvre${n > 1 ? "s n’ont" : " n’a"} pas de valorisation récente`, mission: (c) => `Revalorisation${c ? ` — ${c}` : ""}`, verb: "de revalorisation" },
      insurance:     { head: (n) => `${n} œuvre${n > 1 ? "s" : ""} sans couverture d’assurance vérifiée`, mission: (c) => `Revue d’assurance${c ? ` — ${c}` : ""}`, verb: "de revue d’assurance" },
      transmission:  { head: (n) => `${n} œuvre${n > 1 ? "s" : ""} sans préparation de transmission`, mission: (c) => `Accompagnement à la transmission${c ? ` — ${c}` : ""}`, verb: "de transmission" },
      provenance:    { head: (n) => `${n} œuvre${n > 1 ? "s" : ""} à la provenance incomplète`, mission: (c) => `Recherche de provenance${c ? ` — ${c}` : ""}`, verb: "de recherche de provenance" },
      documentation: { head: (n) => `${n} œuvre${n > 1 ? "s" : ""} sans facture ni certificat`, mission: (c) => `Audit documentaire${c ? ` — ${c}` : ""}`, verb: "d’audit documentaire" },
      conservation:  { head: (n) => `${n} œuvre${n > 1 ? "s" : ""} sans état de conservation renseigné`, mission: (c) => `Revue de conservation${c ? ` — ${c}` : ""}`, verb: "de conservation" },
      patrimonial:   { head: () => "Dossier suffisamment complet pour un rapport patrimonial", mission: (c) => `Rapport patrimonial${c ? ` — ${c}` : ""}`, verb: "de rapport patrimonial" },
    },
  },
  en: {
    collection: "Collection",
    trackedValue: "tracked value",
    worksConcerned: (n) => `${n} artwork${n > 1 ? "s" : ""} concerned`,
    verifyIntro: "This review can be grouped into a mission",
    signals: {
      valuation:     { head: (n) => `${n} artwork${n > 1 ? "s have" : " has"} no recent valuation`, mission: (c) => `Revaluation${c ? ` — ${c}` : ""}`, verb: "revaluation" },
      insurance:     { head: (n) => `${n} artwork${n > 1 ? "s" : ""} without verified insurance cover`, mission: (c) => `Insurance review${c ? ` — ${c}` : ""}`, verb: "insurance review" },
      transmission:  { head: (n) => `${n} artwork${n > 1 ? "s" : ""} with no transmission planning`, mission: (c) => `Transmission support${c ? ` — ${c}` : ""}`, verb: "transmission" },
      provenance:    { head: (n) => `${n} artwork${n > 1 ? "s" : ""} with incomplete provenance`, mission: (c) => `Provenance research${c ? ` — ${c}` : ""}`, verb: "provenance research" },
      documentation: { head: (n) => `${n} artwork${n > 1 ? "s" : ""} without invoice or certificate`, mission: (c) => `Documentary audit${c ? ` — ${c}` : ""}`, verb: "documentary audit" },
      conservation:  { head: (n) => `${n} artwork${n > 1 ? "s" : ""} with no condition recorded`, mission: (c) => `Conservation review${c ? ` — ${c}` : ""}`, verb: "conservation review" },
      patrimonial:   { head: () => "File complete enough for a patrimonial report", mission: (c) => `Patrimonial report${c ? ` — ${c}` : ""}`, verb: "patrimonial report" },
    },
  },
  de: {
    collection: "Sammlung",
    trackedValue: "verfolgter Wert",
    worksConcerned: (n) => `${n} betroffene${n > 1 ? "" : "s"} Werk${n > 1 ? "e" : ""}`,
    verifyIntro: "Diese Prüfung lässt sich in einem Auftrag bündeln",
    signals: {
      valuation:     { head: (n) => `${n} Werk${n > 1 ? "e haben" : " hat"} keine aktuelle Bewertung`, mission: (c) => `Neubewertung${c ? ` — ${c}` : ""}`, verb: "Neubewertung" },
      insurance:     { head: (n) => `${n} Werk${n > 1 ? "e" : ""} ohne geprüften Versicherungsschutz`, mission: (c) => `Versicherungsprüfung${c ? ` — ${c}` : ""}`, verb: "Versicherungsprüfung" },
      transmission:  { head: (n) => `${n} Werk${n > 1 ? "e" : ""} ohne Nachlassvorbereitung`, mission: (c) => `Begleitung der Übertragung${c ? ` — ${c}` : ""}`, verb: "Übertragung" },
      provenance:    { head: (n) => `${n} Werk${n > 1 ? "e" : ""} mit unvollständiger Provenienz`, mission: (c) => `Provenienzrecherche${c ? ` — ${c}` : ""}`, verb: "Provenienzrecherche" },
      documentation: { head: (n) => `${n} Werk${n > 1 ? "e" : ""} ohne Rechnung oder Zertifikat`, mission: (c) => `Dokumentenprüfung${c ? ` — ${c}` : ""}`, verb: "Dokumentenprüfung" },
      conservation:  { head: (n) => `${n} Werk${n > 1 ? "e" : ""} ohne erfassten Zustand`, mission: (c) => `Zustandsprüfung${c ? ` — ${c}` : ""}`, verb: "Zustandsprüfung" },
      patrimonial:   { head: () => "Unterlagen ausreichend für einen Vermögensbericht", mission: (c) => `Vermögensbericht${c ? ` — ${c}` : ""}`, verb: "Vermögensbericht" },
    },
  },
};

const pick = (lang) => COPY[String(lang || "fr").slice(0, 2).toLowerCase()] || COPY.fr;

function fmtValue(value, lang) {
  const v = Number(value) || 0;
  const loc = String(lang || "fr").slice(0, 2) === "de" ? "de-DE" : String(lang || "fr").slice(0, 2) === "en" ? "en-GB" : "fr-FR";
  if (v >= 1_000_000) return `${(v / 1_000_000).toLocaleString(loc, { maximumFractionDigits: 1 })} M€`;
  if (v >= 1_000) return `${Math.round(v / 1_000).toLocaleString(loc)} k€`;
  return `${v.toLocaleString(loc)} €`;
}

// Seuil de déclenchement : au moins 2 œuvres concernées (évite le bruit).
const MIN_COUNT = 2;
const MAX_ARTWORK_IDS = 200;

/**
 * @param {Array} artworks  lignes artworks_secure (+ collection_id)
 * @param {Object} opts { lang, collectionNames: Map<id,title> }
 * @returns {Array} opportunités prêtes à afficher, triées par priorité puis couverture
 */
export function computeAdvisorOpportunities(artworks = [], opts = {}) {
  const lang = opts.lang || "fr";
  const tx = pick(lang);
  const names = opts.collectionNames || new Map();

  // Regrouper par collection (fallback client) — une opportunité porte sur un dossier.
  const groups = new Map();
  for (const a of artworks) {
    const gid = a?.collection_id || a?.client_id || "∅";
    if (!groups.has(gid)) groups.set(gid, []);
    groups.get(gid).push(a);
  }

  const out = [];
  for (const [gid, rows] of groups.entries()) {
    if (!rows.length) continue;
    const collectionId = rows[0]?.collection_id || null;
    const clientId = rows[0]?.client_id || null;
    const collName = (collectionId && names.get(collectionId)) || null;
    const trackedValue = rows.reduce((s, a) => s + artworkValue(a), 0);
    const readiness = computeCollectionReadiness(rows);

    // Signaux "à combler" → opportunités.
    for (const [key, meta] of Object.entries(SIGNAL_META)) {
      const dim = DIM_BY_KEY[key];
      if (!dim) continue;
      const concerned = rows.filter((a) => !dim.test(a));
      const count = concerned.length;
      if (count < MIN_COUNT) continue;
      const sig = tx.signals[key];
      out.push({
        fingerprint: `${gid}:${key}`,
        opportunity_key: key,
        collection_id: collectionId,
        client_id: clientId,
        count,
        tracked_value: trackedValue,
        artwork_ids: concerned.slice(0, MAX_ARTWORK_IDS).map((a) => a.id).filter(Boolean),
        mission_type: meta.missionType,
        mission_title: sig.mission(collName),
        headline: sig.head(count),
        context: `${collName ? `${tx.collection} ${collName}` : tx.collection} · ${tx.trackedValue} ${fmtValue(trackedValue, lang)}`,
        body: `${tx.verifyIntro} ${sig.verb}.`,
        rank: meta.rank,
      });
    }

    // Opportunité positive : dossier complet → rapport patrimonial (§5).
    if (readiness.total >= 6 && readiness.overall >= 80) {
      const sig = tx.signals.patrimonial;
      out.push({
        fingerprint: `${gid}:patrimonial`,
        opportunity_key: "patrimonial",
        collection_id: collectionId,
        client_id: clientId,
        count: readiness.total,
        tracked_value: trackedValue,
        artwork_ids: [],
        mission_type: "curatorial_report",
        mission_title: sig.mission(collName),
        headline: sig.head(),
        context: `${collName ? `${tx.collection} ${collName}` : tx.collection} · ${tx.trackedValue} ${fmtValue(trackedValue, lang)}`,
        body: `${tx.verifyIntro} ${sig.verb}.`,
        rank: 4,
      });
    }
  }

  // Tri : priorité métier, puis couverture d'œuvres, puis valeur suivie.
  out.sort((a, b) => (a.rank - b.rank) || (b.count - a.count) || (b.tracked_value - a.tracked_value));
  return out;
}
